import type { TournamentState } from "./types";

export interface SchemaFieldBase {
  description?: string;
  hidden?: boolean;
}
export interface SchemaFieldString extends SchemaFieldBase {
  type: "string";
  default?: string;
}
export interface SchemaFieldNumber extends SchemaFieldBase {
  type: "number";
  default?: number;
  minimum?: number;
  maximum?: number;
}
export interface SchemaFieldBoolean extends SchemaFieldBase {
  type: "boolean";
  default?: boolean;
}
export type SchemaField = SchemaFieldString | SchemaFieldNumber | SchemaFieldBoolean;
export type FormatSchema = Record<string, SchemaField>;
export type FieldSetting<TField extends SchemaField> = TField["type"] extends "string"
  ? string
  : TField["type"] extends "number"
    ? number
    : TField["type"] extends "boolean"
      ? boolean
      : never;

export type SchemaSettings<TSchema extends FormatSchema> = {
  [K in keyof TSchema]?: FieldSetting<TSchema[K]>;
};

/**
 * Schema for entry data display in the UI
 */
export interface EntryDataField {
  key: string;
  label: string;
  type: "string" | "number" | "boolean";
  description?: string;
  sortable?: boolean;
  value?: (metadata: any) => any;
  formatter?: (value: any) => string;
}

export interface StandingsEntry {
  entryId: string;
  group: string;
  points: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  position?: number;
  metadata?: Record<string, any>;
}

export interface StandingsData {
  groups: Record<string, StandingsEntry[]>;
  settings: Record<string, any>;
}

export abstract class TournamentFormat<TournamentSettingsSchema extends FormatSchema> {
  /**
   * Returns the name of the tournament format
   */
  abstract getName(): string;

  abstract get description(): string;

  /**
   * Returns the entry limits for this format
   */
  abstract getEntryLimits(): { min: number; max: number };

  /**
   * Returns the schema for the settings
   */
  abstract getSettingsSchema(): TournamentSettingsSchema;

  /**
   * Returns the schema for entry data that should be displayed in the UI
   * This defines which fields from format_data should be shown to users
   *
   * @returns Array of entry data field definitions
   */
  abstract getEntryDataSchema(): EntryDataField[];

  /**
   * Returns the schema for match data that should be displayed in the UI
   * This defines which fields from match metadata should be shown to users
   *
   * @returns Array of match data field definitions
   */
  abstract getMatchDataSchema(): EntryDataField[];

  /**
   * Returns the standings data for the tournament
   * This calculates the current standings based on the tournament state
   *
   * @param state - The current tournament state
   * @param settings - Format-specific settings
   * @returns Standings data organized by groups
   */
  getStandings(_state: TournamentState, _settings?: SchemaSettings<TournamentSettingsSchema>): StandingsData {
    return {
      groups: {},
      settings: {},
    };
  }

  hasStandings(): boolean {
    return false;
  }

  /**
   * Transforms the tournament state according to format rules
   *
   * @param state - The current tournament state
   * @param settings - Format-specific settings
   * @returns The transformed tournament state
   */
  /**
   * Transforms the tournament state according to format rules
   *
   * @param state - The current tournament state
   * @param settings - Format-specific settings
   * @returns The transformed tournament state
   *
   * The state includes:
   * - entries: List of entries with their seeds
   * - rounds: Match structure for each round
   * - winners: Final winners when tournament is complete
   * - metadata: Competition-level metadata that persists between transformations
   */
  abstract transform(
    state?: TournamentState,
    settings?: SchemaSettings<TournamentSettingsSchema>,
  ): TournamentState;
}
